import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { useT } from "../i18n/I18nContext";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(t("auth.err_no_token")); return; }
    if (pwd.length < 6) { setError(t("auth.err_pwd_short")); return; }
    if (pwd !== confirmPwd) { setError(t("auth.err_pwd_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md card-mabw p-8 md:p-10 anim-fade-up" data-testid="reset-card">
        <div className="flex items-center gap-3 mb-2">
          <Logo size={40} />
          <span className="font-semibold" style={{ fontFamily: "Outfit" }}>MABW</span>
        </div>

        {done ? (
          <div data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mt-4 mb-4">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("auth.reset_success")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">
              <Link to="/login" className="text-mabw-primary font-medium" data-testid="reset-go-login">
                {t("auth.back_to_login")} →
              </Link>
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-semibold mt-4" style={{ fontFamily: "Outfit" }}>
              {t("auth.reset_title")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">{t("auth.reset_sub")}</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="rp-new">{t("auth.reset_new")}</label>
                <input
                  id="rp-new"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="input-mabw mt-1.5"
                  data-testid="rp-new-input"
                />
                <div className="text-xs text-mabw-soft mt-1">{t("auth.password_hint")}</div>
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="rp-confirm">{t("auth.reset_confirm")}</label>
                <input
                  id="rp-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="input-mabw mt-1.5"
                  data-testid="rp-confirm-input"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="rp-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={busy || !token} className="btn-mabw w-full" data-testid="rp-submit-btn">
                {busy ? t("auth.reset_submitting") : t("auth.reset_submit")}
              </button>
            </form>

            <div className="mt-6 text-sm text-mabw-soft">
              <Link to="/login" className="text-mabw-primary font-medium" data-testid="rp-back-login">
                ← {t("auth.back_to_login")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
